/**
 * @fileoverview AI Settings Component
 * @description User interface for managing AI preferences and BYOK
 * @module components/settings/AISettings
 * @version 2.0.0
 */

import { useMemo, useState } from 'react';
import { useAISettings } from '@/hooks/useAISettings';
import { useGoogleGeminiConnection } from '@/hooks/useGoogleGeminiConnection';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { GEMINI_MODELS, DEFAULT_GEMINI_FLASH_MODEL } from '@flowstate/types';
import { CortexAISetupWizard } from '@flowstate/cortex-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  Bot, 
  ChevronRight,
  Settings2, 
  CheckCircle2,
  ExternalLink,
  Eye,
  EyeOff,
  RotateCcw,
  Loader2,
  Info,
  Unplug,
  Sparkles,
  Terminal,
  KeyRound,
  Package,
} from 'lucide-react';

/** Local CLI manifest — mirrors AgentCLIRegistry without adding a package dependency. */
const CLI_REGISTRY: Array<{
  id: string;
  label: string;
  description: string;
  defaultOn: boolean;
  requiresToken?: 'githubToken';
}> = [
  { id: 'opencode', label: 'OpenCode', description: 'Headless agent server on :4096 — 75+ providers', defaultOn: true },
  { id: 'copilot', label: 'GitHub Copilot CLI', description: 'Requires GitHub token above', defaultOn: false, requiresToken: 'githubToken' },
  { id: 'openclaw', label: 'OpenClaw', description: 'Mirrors agent skills table locally as SKILL.md files', defaultOn: false },
  { id: 'hermes', label: 'Hermes Agent', description: 'Self-improving Postgres-backed learning loop', defaultOn: false },
];

export function AISettings() {
  const { session } = useAuth();
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';

  const {
    settings,
    isLoading,
    updateSettings,
    setGeminiKey,
    setOpenRouterKey,
    setModel,
    setOverride,
    setModelConfig,
    updateBoxPrefs,
    resetSettings,
  } = useAISettings();

  const {
    isConnected: isGoogleGeminiConnected,
    isLoading: isGoogleGeminiLoading,
    connect: connectGoogleGemini,
    disconnect: disconnectGoogleGemini,
  } = useGoogleGeminiConnection();

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showResponseTuning, setShowResponseTuning] = useState(false);
  const [showAgentTools, setShowAgentTools] = useState(false);
  const [githubTokenInput, setGithubTokenInput] = useState('');
  const [showGithubToken, setShowGithubToken] = useState(false);

  // 'flowstate' is a legacy stored value; it no longer means platform-funded inference.
  const selectedProvider = settings.provider === 'flowstate' ? 'gemini' : settings.provider ?? 'gemini';
  const isGeminiProvider = selectedProvider === 'gemini';
  const effectiveGeminiModel = settings.primaryModel || DEFAULT_GEMINI_FLASH_MODEL;
  const isDelegatedGeminiActive = isGoogleGeminiConnected && settings.useGoogleGemini !== false;
  const hasGeminiApiKey = !!settings.geminiApiKey || !!settings.geminiByokOnServer;
  const hasOpenRouterKey = !!settings.openrouterApiKey || !!settings.openrouterApiKeyOnServer;

  const modeLabel = selectedProvider === 'openrouter'
    ? 'OpenRouter'
    : isDelegatedGeminiActive
      ? 'Delegated Google Gemini'
      : hasGeminiApiKey
        ? 'Gemini API key mode'
        : 'No AI access yet';

  const authSourceLabel = selectedProvider === 'openrouter'
    ? 'Using your OpenRouter key'
    : isDelegatedGeminiActive
      ? 'Using your Google account'
      : hasGeminiApiKey
        ? 'Using your Gemini API key'
        : 'Not connected';

  const healthLabel = isGoogleGeminiLoading
    ? 'Checking connection'
    : selectedProvider === 'openrouter'
      ? (hasOpenRouterKey ? 'Healthy with OpenRouter' : 'Action required')
      : isDelegatedGeminiActive
        ? 'Healthy and delegated'
        : hasGeminiApiKey
          ? 'Healthy with Gemini API key'
          : 'Action required';

  const changeSetupSummary = useMemo(() => {
    if (selectedProvider === 'openrouter') {
      return hasOpenRouterKey
        ? 'Cortex is routing through OpenRouter using your API key.'
        : 'OpenRouter is selected but no key is saved yet. Add your OpenRouter key above, or switch back to Gemini.';
    }

    if (isDelegatedGeminiActive) {
      return 'Cortex is calling Gemini through your connected Google account, so usage lands on your own Google quota.';
    }

    if (hasGeminiApiKey) {
      return 'Cortex is calling Gemini with your API key. Connect Google as well if you want to use your Google quota first and keep the key as a fallback.';
    }

    return 'Cortex has no way to reach an AI provider yet. Use the setup above to connect Gemini or OpenRouter.';
  }, [selectedProvider, isDelegatedGeminiActive, hasGeminiApiKey, hasOpenRouterKey]);

  const handleProviderChange = (provider: 'gemini' | 'openrouter') => {
    updateSettings({
      provider,
      useOwnKey: false,
    });
  };

  const handleDisconnectGoogle = () => {
    disconnectGoogleGemini();
    updateSettings({ provider: 'gemini', useGoogleGemini: false });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current AI Mode + Setup Wizard */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-primary" />
                <CardTitle>AI Provider Setup</CardTitle>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">Cortex ready</Badge>
                {isDelegatedGeminiActive && <Badge variant="outline">Delegated</Badge>}
                {selectedProvider === 'openrouter' && <Badge variant="outline">OpenRouter</Badge>}
              </div>
            </div>
            <div className="rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
              {healthLabel}
            </div>
          </div>
          <CardDescription>
            Cortex runs inference on your own AI provider credentials, so you keep your quota, your data, and your bill.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
            <div className="rounded-xl border bg-background p-4 shadow-sm">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Mode</p>
              <div className="mt-3 flex items-center gap-2">
                <p className="text-sm font-semibold text-foreground">{modeLabel}</p>
                {selectedProvider === 'gemini' && <Badge variant="secondary">Recommended</Badge>}
              </div>
            </div>

            <div className="rounded-xl border bg-background p-4 shadow-sm">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Auth source</p>
              <p className="mt-3 text-sm font-semibold text-foreground">{authSourceLabel}</p>
            </div>

            <div className="rounded-xl border bg-background p-4 shadow-sm">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Health</p>
              <p className="mt-3 text-sm font-semibold text-foreground">{healthLabel}</p>
            </div>

            <div className="rounded-xl border bg-background p-4 shadow-sm">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Model routing</p>
              <p className="mt-3 text-sm font-semibold text-foreground">Main chat: {effectiveGeminiModel}</p>
              <p className="mt-1 text-xs text-muted-foreground">Specialists: {settings.subAgentModel || effectiveGeminiModel}</p>
            </div>
          </div>

          <div className="rounded-xl border border-dashed bg-muted/30 p-4 text-sm">
            <p className="font-medium text-foreground">Status summary</p>
            <p className="mt-2 leading-6 text-muted-foreground">{changeSetupSummary}</p>
          </div>

          <CortexAISetupWizard
            supabase={supabase}
            accessToken={session?.access_token ?? ''}
            supabaseUrl={supabaseUrl}
            isGoogleConnected={isGoogleGeminiConnected}
            onConnectGoogle={connectGoogleGemini}
            allowOpenRouterPlatform={false}
            settings={settings}
            onSettingsChanged={updateSettings}
            mode="inline"
          />
        </CardContent>
      </Card>

      {/* Advanced AI configuration */}
      <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
        <div className="rounded-lg border bg-card">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="flex w-full items-center justify-between rounded-lg px-6 py-4 text-left h-auto">
              <div className="flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-semibold">Advanced AI configuration</p>
                  <p className="text-sm font-normal text-muted-foreground">
                    Provider routing, model selection, and credential management.
                  </p>
                </div>
              </div>
              <ChevronRight className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-6 px-6 pb-6">
              {/* Provider switch */}
              <div className="rounded-xl border bg-muted/20 p-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="provider-select">Switch mode</Label>
                  <Select
                    value={selectedProvider}
                    onValueChange={(val) => handleProviderChange(val as 'gemini' | 'openrouter')}
                  >
                    <SelectTrigger id="provider-select" className="w-full">
                      <SelectValue placeholder="Select AI mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gemini">Google Gemini</SelectItem>
                      <SelectItem value="openrouter">OpenRouter</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs leading-5 text-muted-foreground">
                    Gemini is the best-supported path for Cortex. Either way, inference runs on your own credentials.
                  </p>
                </div>

                {isGeminiProvider && (
                  <>
                    <div className="space-y-2 pt-2 border-t">
                      <Label htmlFor="primary-model" className="flex items-center gap-1.5">
                        Main Chat Model
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger><Info className="h-3 w-3 text-muted-foreground" /></TooltipTrigger>
                            <TooltipContent><p>Model used for main Cortex chat responses</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </Label>
                      <Select
                        value={effectiveGeminiModel}
                        onValueChange={(val) => updateSettings({ provider: 'gemini', primaryModel: val })}
                      >
                        <SelectTrigger id="primary-model">
                          <SelectValue placeholder="Select primary model" />
                        </SelectTrigger>
                        <SelectContent>
                          {GEMINI_MODELS.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              <span className="flex items-center gap-2">
                                {m.name}
                                <span className={`text-xs px-1.5 py-0.5 rounded-full ${m.tier === 'free' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {m.tier}
                                </span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {GEMINI_MODELS.find(m => m.id === (settings.primaryModel || DEFAULT_GEMINI_FLASH_MODEL))?.description}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subagent-model" className="flex items-center gap-1.5">
                        Specialist Model
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger><Info className="h-3 w-3 text-muted-foreground" /></TooltipTrigger>
                            <TooltipContent><p>Model used for specialist tools (Tasks, CRM, Accounting, etc.)</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </Label>
                      <Select
                        value={settings.subAgentModel || DEFAULT_GEMINI_FLASH_MODEL}
                        onValueChange={(val) => updateSettings({ provider: 'gemini', subAgentModel: val })}
                      >
                        <SelectTrigger id="subagent-model">
                          <SelectValue placeholder="Select sub-agent model" />
                        </SelectTrigger>
                        <SelectContent>
                          {GEMINI_MODELS.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              <span className="flex items-center gap-2">
                                {m.name}
                                <span className={`text-xs px-1.5 py-0.5 rounded-full ${m.tier === 'free' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {m.tier}
                                </span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="function-model" className="flex items-center gap-1.5">
                        Utility Model
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger><Info className="h-3 w-3 text-muted-foreground" /></TooltipTrigger>
                            <TooltipContent><p>Model used for Brain Dump, AI Enhance, and other utility functions</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </Label>
                      <Select
                        value={settings.functionModel || DEFAULT_GEMINI_FLASH_MODEL}
                        onValueChange={(val) => updateSettings({ provider: 'gemini', functionModel: val })}
                      >
                        <SelectTrigger id="function-model">
                          <SelectValue placeholder="Select function model" />
                        </SelectTrigger>
                        <SelectContent>
                          {GEMINI_MODELS.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              <span className="flex items-center gap-2">
                                {m.name}
                                <span className={`text-xs px-1.5 py-0.5 rounded-full ${m.tier === 'free' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {m.tier}
                                </span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>

              {isGeminiProvider && (
                <div className="rounded-xl border bg-muted/20 p-4 space-y-4">
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-muted-foreground" />
                      Google account
                    </h3>
                    <p className="text-xs leading-5 text-muted-foreground">
                      When connected, Cortex uses your Google Gemini quota. You can still add a fallback API key in the provider setup above.
                    </p>
                  </div>

                  {isGoogleGeminiConnected ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="use-google-gemini-toggle">Use Google account for Gemini</Label>
                          <p className="text-sm text-muted-foreground">
                            Route requests through your Google quota first.
                          </p>
                        </div>
                        <Switch
                          id="use-google-gemini-toggle"
                          checked={settings.useGoogleGemini ?? true}
                          onCheckedChange={(checked) => updateSettings({ provider: 'gemini', useGoogleGemini: checked })}
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDisconnectGoogle}
                        className="text-destructive hover:text-destructive"
                      >
                        <Unplug className="h-4 w-4 mr-2" />
                        Disconnect Google Account
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Connect Google from the provider setup above.
                    </p>
                  )}
                </div>
              )}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      {/* Response tuning */}
      <Collapsible open={showResponseTuning} onOpenChange={setShowResponseTuning}>
        <div className="rounded-lg border bg-card">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="flex w-full items-center justify-between rounded-lg px-6 py-4 text-left h-auto">
              <div className="flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-semibold">Response tuning</p>
                  <p className="text-sm font-normal text-muted-foreground">
                    Fine-tune response style and length only if you need more control.
                  </p>
                </div>
              </div>
              <ChevronRight className={`h-4 w-4 transition-transform ${showResponseTuning ? 'rotate-90' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-6 px-6 pb-6">
              <div className="space-y-3 rounded-xl border bg-muted/20 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label>Creativity</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">
                            Higher values make responses more creative but less predictable.
                            Lower values make responses more focused and deterministic.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <span className="text-sm font-medium">
                    {settings.modelConfig?.temperature?.toFixed(1) ?? '0.7'}
                  </span>
                </div>
                <Slider
                  value={[settings.modelConfig?.temperature || 0.7]}
                  onValueChange={([value]) => setModelConfig({ temperature: value })}
                  min={0}
                  max={2}
                  step={0.1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Focused</span>
                  <span>Balanced</span>
                  <span>Creative</span>
                </div>
              </div>

              <div className="space-y-3 rounded-xl border bg-muted/20 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label>Response Length</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">
                            Maximum length of AI responses. Higher values allow longer responses
                            but use more API credits.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <span className="text-sm font-medium">
                    {settings.modelConfig?.maxTokens || 2048}
                  </span>
                </div>
                <Slider
                  value={[settings.modelConfig?.maxTokens || 2048]}
                  onValueChange={([value]) => setModelConfig({ maxTokens: value })}
                  min={256}
                  max={8192}
                  step={256}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Short (256)</span>
                  <span>Medium (2048)</span>
                  <span>Long (8192)</span>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      {/* Agent Tools */}
      <Collapsible open={showAgentTools} onOpenChange={setShowAgentTools}>
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center justify-between w-full p-4 rounded-lg text-left h-auto"
              aria-expanded={showAgentTools}
              aria-controls="agent-tools-content"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-indigo-500/10">
                  <Terminal className="h-5 w-5 text-indigo-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Agent Box — CLI Tools</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Configure which agent CLIs are installed in your personal cloud box
                  </p>
                </div>
              </div>
              <ChevronRight
                className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${showAgentTools ? 'rotate-90' : ''}`}
              />
            </Button>
          </CollapsibleTrigger>

          <CollapsibleContent id="agent-tools-content">
            <div className="px-4 pb-4 space-y-5 border-t border-border pt-4">

              {/* GitHub Token */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-muted-foreground" />
                  <label htmlFor="github-token-input" className="text-sm font-medium">
                    GitHub Personal Access Token
                  </label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Required to authenticate the GitHub Copilot CLI inside your box. Stored locally only — never sent to FlowState servers.
                </p>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      id="github-token-input"
                      type={showGithubToken ? 'text' : 'password'}
                      value={githubTokenInput}
                      onChange={(e) => setGithubTokenInput(e.target.value)}
                      placeholder={settings.box?.githubToken ? '••••••••••••••••' : 'ghp_xxxxxxxxxxxx'}
                      className="w-full h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm pr-10 focus:outline-none focus:ring-1 focus:ring-ring"
                      aria-describedby="github-token-desc"
                    />
                    <button
                      type="button"
                      onClick={() => setShowGithubToken((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                      aria-label={showGithubToken ? 'Hide token' : 'Show token'}
                    >
                      {showGithubToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!githubTokenInput.trim()}
                    onClick={() => {
                      updateBoxPrefs({ githubToken: githubTokenInput.trim() });
                      setGithubTokenInput('');
                    }}
                  >
                    Save
                  </Button>
                  {settings.box?.githubToken && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => updateBoxPrefs({ githubToken: undefined })}
                      className="text-destructive hover:text-destructive"
                    >
                      Clear
                    </Button>
                  )}
                </div>
                {settings.box?.githubToken && (
                  <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    <span>Token configured</span>
                  </div>
                )}
              </div>

              {/* CLI Toggles */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Installed CLIs</span>
                </div>

                <div className="space-y-3 rounded-md border border-border p-3">
                  {/* Gemini — always on */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Gemini CLI</p>
                      <p className="text-xs text-muted-foreground">Free Gemini access via your Google account</p>
                    </div>
                    <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-600 dark:text-green-400">
                      Always on
                    </span>
                  </div>

                  {CLI_REGISTRY.map((cli) => {
                    const hasToken = cli.requiresToken
                      ? !!settings.box?.[cli.requiresToken]
                      : true;
                    return (
                      <div key={cli.id} className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">{cli.label}</p>
                          <p className="text-xs text-muted-foreground">
                            {cli.description}
                            {cli.requiresToken && !hasToken && (
                              <span className="ml-1 text-amber-500">— token not set</span>
                            )}
                          </p>
                        </div>
                        <Switch
                          checked={settings.box?.installCLIs?.[cli.id] ?? cli.defaultOn}
                          onCheckedChange={(checked) =>
                            updateBoxPrefs({
                              installCLIs: { ...settings.box?.installCLIs, [cli.id]: checked },
                            })
                          }
                          disabled={!hasToken}
                          aria-label={`Toggle ${cli.label}`}
                        />
                      </div>
                    );
                  })}
                </div>

                <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  Changes take effect the next time your Box is provisioned or resumed.
                </p>
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      {/* Reset Button */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={resetSettings}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset to Defaults
        </Button>
      </div>
    </div>
  );
}

export default AISettings;
